import React from 'react'
import { IViewPortColumn, IAggregateTypes, ISpreadsheetContextMenuState } from 'components/spreadsheet/types'
import { calculateAggregate } from 'components/spreadsheet/helpers/aggregate'
import { ITableRow } from 'types'
import { formatPercentDisplayValue } from 'components/spreadsheet/helpers/format'
import { aggregateStyles } from 'components/spreadsheet/components/cell/styles'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { getRowValue, rowDataArrayToObject } from 'components/spreadsheet/helpers/functions'
import { NUMBER_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import Skeleton from 'components/skeleton'

const styles = aggregateStyles()

interface AggregateProps {
  cumulativeWidth: number
  width: number
  frozen: boolean
  isLastFrozenColumn: boolean
  className?: string
  style?: React.CSSProperties
  height: number
  loading?: boolean
  rows?: ITableRow[]
  column?: IViewPortColumn
  columnNumber?: number
  optionClick?: (menuState: ISpreadsheetContextMenuState) => void
  // Pre-computed aggregate value to avoid passing all rows to each Aggregate component
  computedValue?: string
}

const Aggregate: React.FC<AggregateProps> = ({
  cumulativeWidth,
  width,
  frozen,
  isLastFrozenColumn,
  className,
  style: styleProp,
  height,
  loading,
  rows,
  column,
  columnNumber,
  optionClick,
  computedValue
}) => {
  const { spreadsheetData } = useDataContext()

  // Derive the aggregate value synchronously to avoid a one-frame delay from useEffect
  let value = ''
  if (computedValue !== undefined) {
    value = computedValue
  } else if (!loading && column && rows) {
    // Legacy path: compute from rows (kept for CollapsedRow aggregates)
    const overwrittenAggregate =
      columnNumber === 0 && (column.column.aggregate === undefined || column.column.aggregate === IAggregateTypes.None)
        ? IAggregateTypes['# Rows']
        : column.column.aggregate
    if (overwrittenAggregate !== undefined && overwrittenAggregate !== IAggregateTypes.None) {
      let newRows = rows.map((row) =>
        getRowValue(row, column.column.publicId, spreadsheetData.columnIdToIndex, spreadsheetData.columnStore)
      )
      const rowsData = rows.map((row) =>
        rowDataArrayToObject(row.rowData, spreadsheetData.indexToColumnId, row._rowIndex, spreadsheetData.columnStore)
      )
      if (column && column.column.kind === 'checkbox') {
        newRows = newRows.map((newRow) => {
          return !newRow || newRow === 'false' ? null : newRow
        })
      }
      value = calculateAggregate(
        newRows,
        overwrittenAggregate,
        spreadsheetData.totalRows,
        column,
        rowsData,
        spreadsheetData.columnValuesCount
      )
    }
  }

  const displayValue =
    column &&
    !isNaN(Number.parseFloat(value)) &&
    column.column.kind === 'percentage' &&
    (column.column.aggregate === IAggregateTypes.Sum ||
      column.column.aggregate === IAggregateTypes.Average ||
      column.column.aggregate === IAggregateTypes.Min ||
      column.column.aggregate === IAggregateTypes.Max ||
      column.column.aggregate === IAggregateTypes.Range)
      ? formatPercentDisplayValue(Number.parseFloat(value))
      : value

  const isSticky = frozen

  return (
    <div
      className={styles.container({
        frozen: isSticky,
        lastFrozen: isLastFrozenColumn,
        clickable: optionClick !== undefined,
        class: className
      })}
      onClick={(event) => {
        if (optionClick && !loading) {
          event.preventDefault()
          optionClick({
            open: true,
            top: `${event.clientY + 5}px`,
            left: `${event.clientX - 120}px`,
            right: 'auto',
            bottom: 'auto',
            columnNumber: column ? column.originalPosition : -1,
            columnId: column ? column.column.publicId : '',
            rowNumber: -1,
            rowId: ''
          })
        }
      }}
      style={{
        width: width ? `${width}px` : '100%',
        height: `${height}px`,
        left: `${cumulativeWidth}px`,
        ...styleProp
      }}
    >
      {loading && !column && (
        <div className="flex items-center absolute w-full h-full box-inherit py-[5px] px-[8px]">
          <Skeleton className="block h-3 w-1/2" />
        </div>
      )}
      {!loading && (
        // The footer reads like the header: 13px semibold.
        <div
          className="flex items-center text-[13px] font-semibold absolute w-full h-full py-[5px] px-[8px]"
          style={{
            justifyContent: column && NUMBER_COLUMN_TYPES.includes(column.column.kind) ? 'flex-end' : 'flex-start',
            textDecoration: styleProp?.textDecoration
          }}
        >
          {column && column.column.aggregate !== IAggregateTypes.None && (
            <div>{`${IAggregateTypes[column.column.aggregate]}: ${displayValue}`}</div>
          )}
          {column &&
            columnNumber === 0 &&
            (column.column.aggregate === undefined || column.column.aggregate === IAggregateTypes.None) && (
              <div>{`${IAggregateTypes[IAggregateTypes['# Rows']]}: ${value}`}</div>
            )}
        </div>
      )}
    </div>
  )
}

export default Aggregate
